import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDistrict } from '../district';
import { useCurrentSession, useMembershipSessions } from '../sessions';
import { buildRecoveryQrLines, buildRecoveryUrl, generateQrDataUrl } from './qr';
import { persistRecoveryCode } from './recoveryCodeStorage';
import { useAnonymousAuth } from './useAnonymousAuth';
import { useIssueRecoveryCode } from './useIssueRecoveryCode';
import { useResolvedNickname } from './useResolvedNickname';

/**
 * U1b ユーザー復帰コード発行
 *
 * old2 `apps/user/src/pages/RecoveryIssuePage.tsx` + `shared-i18n/ja/user.json#recovery` を逐語移植。
 *
 * v2 調整点:
 *   - **発行式の差**: old2 は client が `generateRecoveryCode()` で生成 → `setRecoveryCode` callable に
 *     送信していたが、v2 は `issueRecoveryCode` callable がサーバで生成して返す。よって mount 時に
 *     `call()` で発行 → success state の `recoveryCode` を表示。
 *   - **「保存した、次へ」**: 発行はすでにサーバ側で完了済 (recoveryCodes/{uid})。よって本ボタンでは
 *     localStorage への code 保存 (EntryPage の「発行済」判定 + RecoveryAutoPage 自動復帰の安全網) と
 *     map への遷移のみ行う。
 *   - 文言は user.json#recovery を逐語ハードコード (v2 i18n は同 namespace を保持しないため)。
 *   - 遷移先は old2 `/app/map` → v2 主画面 `/map`。
 */

const RECOVERY_TEXT = {
  issueTitle: 'あなたのユーザー復帰コード',
  issueIntro: '別のスマホや、しばらく経った後でも自分の投稿を編集できるようになります。',
  warnSave: 'このコードを保管してください',
  saveOptions: ['スクショで保存', '紙にメモ', '印刷'],
  warnLost: '紛失すると復元できません（個人情報を取得しないため）',
  saved: '保存した、次へ',
  skip: '今はスキップする',
  skipConfirm:
    'コードなしで進むと、別の端末や数日後に投稿の編集ができなくなる場合があります。よろしいですか？',
} as const;

export function RecoveryIssuePage() {
  const navigate = useNavigate();
  const { urlToken } = useParams();
  const district = useDistrict();
  // 地区フォールバック根絶: 地区未解決時は名称空・persist は地区確定後のみ (旧 phase2-default 撤去)。
  const districtName = district?.name ?? '';
  const { state, call } = useIssueRecoveryCode();

  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // mount 時にサーバへコード発行を依頼 (idle かつ地区確定時のみ、StrictMode 二重呼出は state ガードで回避)。
  // 解釈B: コードは地区 (membership) 単位で発行するため対象地区 id を渡す。
  useEffect(() => {
    if (state.status === 'idle' && district?.id) {
      void call(district.id);
    }
  }, [state.status, call, district?.id]);

  const code = state.status === 'success' ? state.data.recoveryCode : null;

  // 2026-09-04: 画像だけで「誰の・どの参加セッションの復帰用か」が分かるよう、ニックネームと
  // 参加セッションも焼き込む (QR が符号化する URL は不変)。直前の onboarding で保存済の値を使う。
  const auth = useAnonymousAuth();
  const uid = auth.status === 'authenticated' ? auth.user.uid : null;
  // 端末内の保存値が空でも参加登録の名前をカードに印字する (2026-10-09)。
  const nickname = useResolvedNickname(uid, district?.id ?? null);
  const { joinedSessionIds } = useMembershipSessions(uid, district?.id ?? null);
  const { state: sessionState } = useCurrentSession(district?.id ?? null);
  const joinedSessionNames = useMemo(() => {
    const items = sessionState.status === 'ready' ? sessionState.items : [];
    return joinedSessionIds
      .map((id) => items.find((s) => s.id === id)?.name)
      .filter((n): n is string => !!n);
  }, [joinedSessionIds, sessionState]);

  // 発行成功時に QR (地区名 caption + 本人情報 + コード subCaption 焼込み) を生成。
  // 発行直後の画面なので、ニックネーム / 参加セッションが読み込まれ次第そのまま反映してよい
  // (ユーザー設定側と違い、まだ保存・共有されていない画像のため確認は挟まない)。
  useEffect(() => {
    if (!code || !urlToken) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQrUrl(null);
      return;
    }
    let cancelled = false;
    const url = buildRecoveryUrl(urlToken, code);
    void generateQrDataUrl(url, {
      caption: districtName ? `📍 ${districtName}` : undefined,
      lines: buildRecoveryQrLines(nickname, joinedSessionNames),
      subCaption: code,
    }).then((u) => {
      if (!cancelled) setQrUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [code, urlToken, districtName, nickname, joinedSessionNames]);

  function persistAndProceed() {
    // コードは発行時点で membership (recoveryCodeHash) に保存済。ここでは local 保存 + 遷移のみ。
    if (code && district) persistRecoveryCode(district.id, code);
    navigate(`/m/${urlToken}/map`);
  }

  function skipAndProceed() {
    if (!confirm(RECOVERY_TEXT.skipConfirm)) {
      return;
    }
    navigate(`/m/${urlToken}/map`);
  }

  async function copyCode() {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        alert('コピーできませんでした。手動で控えてください。');
      }
    }
  }

  return (
    <main className="mx-auto max-w-md p-6">
      <h1 className="text-2xl font-bold mt-6 mb-1">🔑 {RECOVERY_TEXT.issueTitle}</h1>
      <p className="text-sm text-slate-700 mb-4">📍 {districtName} のユーザー復帰コード</p>
      <p className="text-slate-700 mb-6">{RECOVERY_TEXT.issueIntro}</p>

      <div className="bg-slate-100 p-6 rounded-lg text-center mb-3">
        {code ? (
          <code className="text-xl font-mono select-all tracking-wider">{code}</code>
        ) : state.status === 'error' ? (
          <span className="text-danger-700 text-sm">{state.error.message}</span>
        ) : (
          <span className="text-slate-400">発行中…</span>
        )}
      </div>
      <button
        onClick={copyCode}
        type="button"
        disabled={!code}
        className="w-full mb-4 text-sm py-3 border border-slate-300 rounded-md hover:bg-slate-50 font-medium disabled:opacity-40"
      >
        {copied ? '✓ コピーしました' : '📋 コードをコピー'}
      </button>

      <div className="bg-white border border-slate-200 mb-4 flex items-center justify-center min-h-[280px]">
        {qrUrl ? (
          <img src={qrUrl} alt="ユーザー復帰コードQR" className="max-w-full" />
        ) : (
          <span className="text-slate-400">QR生成中…</span>
        )}
      </div>

      <p className="text-sm text-primary-700 mb-2">
        📱 QR を次回スマホで読み込めば、自動でログインできます。
      </p>
      <p className="text-sm text-warning-600 mb-2">⚠ {RECOVERY_TEXT.warnSave}</p>
      <ul className="text-sm text-slate-700 list-disc list-inside mb-4">
        {RECOVERY_TEXT.saveOptions.map((o) => (
          <li key={o}>{o}</li>
        ))}
      </ul>
      <p className="text-sm text-warning-600 mb-6">⚠ {RECOVERY_TEXT.warnLost}</p>

      <button
        onClick={persistAndProceed}
        disabled={!code}
        className="w-full bg-primary-600 text-white py-4 rounded-md font-medium disabled:opacity-40"
      >
        {RECOVERY_TEXT.saved}
      </button>
      <button onClick={skipAndProceed} type="button" className="w-full mt-2 text-slate-700 py-3">
        {RECOVERY_TEXT.skip}
      </button>
    </main>
  );
}
